import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Chemistry of Life",
  examWeight: "8–11%",
  focus: "Energetics, Information Storage, and Systems Interactions",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Structure of Water and Hydrogen Bonding",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💧 The Structure of Water (<InlineMath math="H_2O" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Water is composed of two hydrogen atoms covalently bonded to one oxygen atom. Its biological importance stems from its polarity.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Polarity</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Oxygen is highly <strong>electronegative</strong>, pulling shared electrons closer. This creates a partial negative charge (<InlineMath math="\delta-" />) on oxygen and partial positive charges (<InlineMath math="\delta+" />) on hydrogens.
                </p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">Hydrogen Bonding</h4>
                <p className="text-sm text-slate-700 mt-1">
                  A weak interaction between the <InlineMath math="\delta-" /> region of one molecule and the <InlineMath math="\delta+" /> region of <em>another</em>. Intramolecular bonds are covalent (strong); intermolecular bonds are hydrogen bonds (weak).
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 Emergent Properties of Water</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-cyan-100 to-white p-4 rounded-lg border border-cyan-200">
                  <h4 className="font-bold text-cyan-900">🔗 Cohesion & Adhesion</h4>
                  <p className="text-xs text-slate-700 mt-1">
                    <strong>Cohesion:</strong> Attraction between water molecules. Creates <strong>surface tension</strong>.
                    <br />
                    <strong>Adhesion:</strong> Attraction to other polar surfaces. Enables <strong>capillary action</strong> in plant xylem.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-100 to-white p-4 rounded-lg border border-red-200">
                  <h4 className="font-bold text-red-900">🌡️ High Specific Heat</h4>
                  <p className="text-xs text-slate-700 mt-1">
                    Water resists temperature changes because H-bonds must absorb significant energy to break. This moderates Earth's climate and maintains <strong>homeostasis</strong>.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-orange-100 to-white p-4 rounded-lg border border-orange-200">
                  <h4 className="font-bold text-orange-900">💨 Evaporative Cooling</h4>
                  <p className="text-xs text-slate-700 mt-1">
                    High heat of vaporization means evaporation removes significant heat energy, preventing overheating in organisms (sweating/transpiration).
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-white p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-900">🧊 Density of Ice</h4>
                  <p className="text-xs text-slate-700 mt-1">
                    Water expands upon freezing into a crystal lattice. Ice is <strong>less dense</strong> than liquid water, floating to insulate aquatic life in winter.
                  </p>
                </div>
              </div>
              <KeyTerm 
                term="Solvent Versatility" 
                definition="Known as the 'Universal Solvent' because it dissolves polar and ionic substances by forming hydration shells. Nonpolar substances (hydrophobic) do not dissolve."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Elements of Life",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Essential Elements (CHONPS)</h3>
            <p className="text-slate-700 mb-4">Organisms exchange matter with the environment to grow and reproduce. Key elements include:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <span className="font-bold block">Carbon (C)</span>
                <span className="text-xs">All Macromolecules</span>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <span className="font-bold block">Nitrogen (N)</span>
                <span className="text-xs">Proteins & Nucleic Acids</span>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <span className="font-bold block">Phosphorus (P)</span>
                <span className="text-xs">Nucleic Acids & Lipids</span>
              </div>
            </div>
            <p className="text-sm text-slate-600 italic">Hydrogen (H) and Oxygen (O) are in all; Sulfur (S) is found in Proteins.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚛️ The Role of Carbon</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Tetravalence:</strong> Carbon has 4 valence electrons, allowing 4 covalent bonds.</li>
              <li><strong>Diversity:</strong> Can form chains, rings, and branched structures with single, double, or triple bonds.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Introduction to Macromolecules",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <h4 className="font-bold text-emerald-900">Dehydration Synthesis</h4>
              <p className="text-sm text-slate-700 mt-2"><strong>Builds polymers.</strong> Removes <InlineMath math="-H" /> and <InlineMath math="-OH" /> to form <InlineMath math="H_2O" /> as a byproduct, creating a covalent bond.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
              <h4 className="font-bold text-rose-900">Hydrolysis</h4>
              <p className="text-sm text-slate-700 mt-2"><strong>Breaks polymers.</strong> Adds <InlineMath math="H_2O" /> to break a covalent bond, splitting the water into <InlineMath math="-H" /> and <InlineMath math="-OH" />.</p>
            </div>
          </div>
          <ImportantNote>
            Example: Digestion of food involves hydrolysis; building muscle involves dehydration synthesis.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Carbohydrates",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🍞 Structure and Function</h3>
            <div className="space-y-3">
              <KeyTerm term="Monomer" definition="Monosaccharides (Glucose, Fructose) with a C:H:O ratio of 1:2:1." />
              <KeyTerm term="Bond Type" definition="Glycosidic Linkage (covalent bond formed via dehydration synthesis)." />
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Energy Storage</h4>
                <ul className="text-xs space-y-2 mt-2 text-slate-700">
                  <li><strong>Starch:</strong> Plant storage (branched/unbranched glucose chains).</li>
                  <li><strong>Glycogen:</strong> Animal storage (highly branched; in liver/muscle).</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Structural Support</h4>
                <ul className="text-xs space-y-2 mt-2 text-slate-700">
                  <li><strong>Cellulose:</strong> Plant cell walls (linear glucose cables; difficult to digest).</li>
                  <li><strong>Chitin:</strong> Fungal cell walls and arthropod exoskeletons.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Lipids",
      content: (
        <div className="space-y-6">
          <ImportantNote>
            Lipids are <strong>Nonpolar</strong> and <strong>Hydrophobic</strong>. They do not form true polymers.
          </ImportantNote>
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧈 Types of Lipids</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Triglycerides (Fats/Oils)</h4>
                <p className="text-sm text-slate-700">1 Glycerol + 3 Fatty Acid chains. Used for long-term energy, insulation, and cushioning.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Phospholipids</h4>
                <p className="text-sm text-slate-700">1 Glycerol + 2 Fatty Acid tails (hydrophobic) + 1 Phosphate head (hydrophilic). They are <strong>amphipathic</strong> and form the lipid bilayer.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Steroids</h4>
                <p className="text-sm text-slate-700">Four fused carbon rings. Examples: Cholesterol (membrane fluidity), Testosterone, Estrogen.</p>
              </div>
            </div>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛓️ Saturation of Fatty Acids</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 border rounded bg-white">
                <h5 className="font-bold text-slate-800 underline">Saturated</h5>
                <p className="text-xs text-slate-600 mt-1">Single carbon bonds. Straight chains. Solid at room temperature (Butter).</p>
              </div>
              <div className="p-3 border rounded bg-white">
                <h5 className="font-bold text-slate-800 underline">Unsaturated</h5>
                <p className="text-xs text-slate-600 mt-1">Contains <InlineMath math="C=C" /> double bonds. "Kinked" chains. Liquid at room temperature (Oils).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Nucleic Acids",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Nucleotide Structure</h3>
            <p className="text-sm text-slate-700 mb-4">The monomer consists of: <strong>1. Phosphate Group</strong>, <strong>2. Pentose Sugar</strong>, <strong>3. Nitrogenous Base</strong>.</p>
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-300">
              <h4 className="font-bold text-slate-900 mb-2">Directionality</h4>
              <p className="text-sm text-slate-700">
                <strong>5' End:</strong> Phosphate group. <br />
                <strong>3' End:</strong> Hydroxyl (<InlineMath math="-OH" />) group. <br />
                Synthesis always adds nucleotides to the <strong>3' end</strong>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 DNA vs. RNA</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded border border-red-200 bg-red-50">
                <h4 className="font-bold text-red-900">DNA</h4>
                <ul className="text-xs list-disc pl-4 mt-2 space-y-1">
                  <li>Sugar: Deoxyribose</li>
                  <li>Bases: A, T, C, G</li>
                  <li>Double-stranded helix</li>
                  <li><strong>Antiparallel</strong> orientation</li>
                </ul>
              </div>
              <div className="p-4 rounded border border-blue-200 bg-blue-50">
                <h4 className="font-bold text-blue-900">RNA</h4>
                <ul className="text-xs list-disc pl-4 mt-2 space-y-1">
                  <li>Sugar: Ribose</li>
                  <li>Bases: A, U, C, G</li>
                  <li>Usually single-stranded</li>
                  <li>Transmits genetic info</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Nitrogenous Bases</h3>
            <div className="space-y-3">
              <Mnemonic title="Purines" content="Pure As Gold: Adenine (A) and Guanine (G). Double-ring structure." />
              <div className="bg-white p-4 rounded border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 text-sm">Base Pairing Rules</h4>
                <p className="text-sm text-slate-700"><strong>A-T (or U):</strong> 2 H-bonds. <br /><strong>C-G:</strong> 3 H-bonds.</p>
                <p className="text-xs text-red-700 mt-2 font-semibold">High C-G content increases stability at high temperatures due to the extra hydrogen bond.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Proteins",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🥩 Amino Acid Structure</h3>
            <p className="text-sm text-slate-700 mb-4">Monomers have a central Carbon bonded to H, an Amine (<InlineMath math="NH_2" />), a Carboxyl (<InlineMath math="COOH" />), and a variable <strong>R-group</strong>.</p>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
              <h4 className="font-bold text-yellow-900 text-sm">R-Group Behaviors</h4>
              <ul className="text-xs list-disc pl-5 mt-1 space-y-1">
                <li><strong>Hydrophobic:</strong> Nonpolar; folds inward.</li>
                <li><strong>Hydrophilic:</strong> Polar/Charged; moves toward water.</li>
                <li><strong>Ionic:</strong> Charged; forms ionic bonds.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🪜 Levels of Protein Structure</h3>
            <div className="space-y-4">
              <div className="flex gap-4 p-3 bg-white border rounded shadow-sm">
                <div className="font-bold text-indigo-600">1°</div>
                <div className="text-sm text-slate-700"><strong>Primary:</strong> Linear sequence of amino acids via <strong>Peptide Bonds</strong>.</div>
              </div>
              <div className="flex gap-4 p-3 bg-white border rounded shadow-sm">
                <div className="font-bold text-indigo-600">2°</div>
                <div className="text-sm text-slate-700"><strong>Secondary:</strong> Alpha-helices/Beta-pleated sheets via <strong>H-bonds</strong> in the backbone.</div>
              </div>
              <div className="flex gap-4 p-3 bg-white border rounded shadow-sm">
                <div className="font-bold text-indigo-600">3°</div>
                <div className="text-sm text-slate-700"><strong>Tertiary:</strong> Overall 3D shape via <strong>R-group interactions</strong> (hydrophobic, ionic, H-bonds, disulfide bridges).</div>
              </div>
              <div className="flex gap-4 p-3 bg-white border rounded shadow-sm">
                <div className="font-bold text-indigo-600">4°</div>
                <div className="text-sm text-slate-700"><strong>Quaternary:</strong> Multiple polypeptide chains interacting (e.g., Hemoglobin).</div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Denaturation</h3>
            <p className="text-sm text-slate-700">Protein loses its 3D shape and biological activity due to <strong>heat</strong> or <strong>pH</strong> changes. Structure determines function; if the shape is lost, the protein cannot function.</p>
            <ImportantNote>
              Denaturation usually leaves the <strong>primary structure</strong> (peptide bonds) intact.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit1Notes;